import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const PMPOfficeForm = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  const [formData, setFormData] = useState({
    customer_id: '',
    service_location_id: '',
    building_name: '',
    address: '',
    date_of_inspection: new Date().toISOString().split('T')[0],
    inspector_name: user?.name || '',
    floor_number: '',
    area_type: 'exterior',
    inspection_items: [],
    additional_notes: '',
    photos: [],
    signature: '',
    forward_to_operations: false
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchLocations = async (customerId) => {
    try {
      const response = await axios.get(`${API}/service-locations/by-customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations');
      setLocations([]);
    }
  };

  const handleCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId);
    setFormData({ ...formData, customer_id: customerId, service_location_id: '', building_name: '', address: '' });
    if (customerId) {
      fetchLocations(customerId);
    } else {
      setLocations([]);
    }
  };

  const handleLocationChange = (locationId) => {
    const location = locations.find(loc => loc.id === locationId);
    if (location) {
      setFormData({
        ...formData,
        service_location_id: locationId,
        building_name: location.location_name,
        address: `${location.address}, ${location.city}, ${location.state}`
      });
    }
  };

  // Inspection points per area type
  const inspectionPoints = {
    exterior: [
      'Lights & Fixtures',
      'Security',
      'Fencing & Gates',
      'Painting',
      'Cleanliness / Vandalism',
      'Signage',
      'Parking Lot',
      'All Exits',
      'Door Locks / Hardware',
      'Gutters & Building Drains',
      'Asphalt & Parking Lots',
      'Windows',
      'Irrigation Systems',
      'Landscaping'
    ],
    lobby: [
      'Lights & Fixtures',
      'Exit/Egress Lighting',
      'Ceiling Tiles',
      'Flooring (Tile, Carpet, VCT)',
      'Walls / Painting',
      'Doors / Locks / Hardware',
      'HVAC Vents / Filters',
      'Windows',
      'Fire Extinguishers',
      'Emergency Lighting',
      'Reception Area',
      'Elevators',
      'Directory Signage'
    ],
    office_space: [
      'Lights & Fixtures',
      'Ceiling Tiles',
      'Flooring',
      'Walls / Painting',
      'Doors / Locks / Hardware',
      'HVAC Vents / Filters',
      'Windows',
      'Emergency Lighting',
      'Workstations',
      'Conference Rooms',
      'Break Room'
    ],
    restroom: [
      'Lights & Fixtures',
      'Ceiling Tiles',
      'Flooring',
      'Walls / Tiles',
      'Doors / Locks / Hardware',
      'HVAC Vents / Exhaust',
      'Plumbing Fixtures',
      'Sinks & Faucets',
      'Toilets',
      'Dispensers (Soap, Towels)',
      'Mirrors',
      'Cleanliness'
    ],
    common_area: [
      'Lights & Fixtures',
      'Ceiling Tiles',
      'Flooring',
      'Walls / Painting',
      'Doors / Locks / Hardware',
      'HVAC Vents / Filters',
      'Electrical Rooms',
      'Breaker Panels',
      'Plumbing (General)',
      'Kitchen Appliances',
      'Exhaust Fans',
      'Janitor Closets',
      'Storage Rooms',
      'Windows',
      'Fire Extinguishers',
      'Emergency Lighting',
      'Hallways & Stairwells',
      'Drinking Fountains'
    ]
  };

  useEffect(() => {
    // Initialize inspection items when area type changes
    const points = inspectionPoints[formData.area_type] || [];
    setFormData({
      ...formData,
      inspection_items: points.map(item => ({
        item,
        status: 'satisfactory',
        photo: null,
        description: ''
      }))
    });
  }, [formData.area_type]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.inspection_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, inspection_items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/pmp-office`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Office PM inspection submitted successfully');
      navigate('/field/pmp-office');
    } catch (error) {
      toast.error('Failed to submit inspection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Card className="mb-6" style={{ backgroundColor: '#D3D3D3' }}>
        <CardHeader>
          <img 
            src="https://customer-assets.emergentagent.com/job_tfs-workspace/artifacts/xuj2h70u_TFSI%203-26%20PNG.png" 
            alt="TFS Logo" 
            className="h-16 mx-auto mb-4"
          />
          <CardTitle className="text-center text-2xl">
            PREVENTIVE MAINTENANCE – OFFICE
          </CardTitle>
          <p className="text-center font-medium">
            MANTENIMIENTO PREVENTIVO – OFICINA
          </p>
        </CardHeader>
      </Card>

      <form onSubmit={handleSubmit}>
        {/* Header Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Inspection Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Customer / Cliente *</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={selectedCustomerId}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                  required
                >
                  <option value="">-- Select Customer / Seleccionar Cliente --</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Building / Branch / Edificio / Sucursal *</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={formData.service_location_id}
                  onChange={(e) => handleLocationChange(e.target.value)}
                  disabled={!selectedCustomerId}
                  required
                >
                  <option value="">-- Select Location / Seleccionar Ubicación --</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.location_name} - {location.city}, {location.state}
                    </option>
                  ))}
                </select>
                {!selectedCustomerId && (
                  <p className="text-sm text-gray-500 mt-1">Please select a customer first</p>
                )}
              </div>

              <div className="md:col-span-2">
                <Label>Address / Dirección</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  readOnly
                  className="bg-gray-100"
                  placeholder="Auto-filled from location selection"
                />
              </div>

              <div>
                <Label>Date of Inspection / Fecha *</Label>
                <Input
                  type="date"
                  value={formData.date_of_inspection}
                  onChange={(e) => setFormData({ ...formData, date_of_inspection: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>Inspector Name / Inspector *</Label>
                <Input
                  value={formData.inspector_name}
                  onChange={(e) => setFormData({ ...formData, inspector_name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>Floor Number / Número de Piso</Label>
                <Input
                  value={formData.floor_number}
                  onChange={(e) => setFormData({ ...formData, floor_number: e.target.value })}
                  placeholder="e.g., 1st Floor, 2nd Floor"
                />
              </div>

              <div>
                <Label>Area Type / Tipo de Área *</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={formData.area_type}
                  onChange={(e) => setFormData({ ...formData, area_type: e.target.value })}
                  required
                >
                  <option value="exterior">Exterior</option>
                  <option value="lobby">Lobby / Vestíbulo</option>
                  <option value="office_space">Office Space / Espacio de Oficina</option>
                  <option value="restroom">Restroom / Baño</option>
                  <option value="common_area">Common Area / Área Común</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inspection Items */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Inspection Points / Puntos de Inspección</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {formData.inspection_items.map((item, index) => (
                <div key={index} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="font-medium">{item.item}</Label>
                    <select
                      className="border rounded px-2 py-1"
                      value={item.status}
                      onChange={(e) => handleItemChange(index, 'status', e.target.value)}
                    >
                      <option value="satisfactory">✓ Satisfactory / Satisfactorio</option>
                      <option value="needs_attention">⚠ Needs Attention / Requiere Atención</option>
                      <option value="unsatisfactory">✗ Unsatisfactory / Insatisfactorio</option>
                    </select>
                  </div>
                  {item.status !== 'satisfactory' && (
                    <Input
                      placeholder="Description / Descripción"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className="mt-2"
                    />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Additional Notes */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Additional Notes / Notas Adicionales</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              className="w-full border rounded-md px-3 py-2"
              rows={4}
              value={formData.additional_notes}
              onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
              placeholder="Any additional comments..."
            />
          </CardContent>
        </Card>

        {/* Forward to Operations */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="forward"
                checked={formData.forward_to_operations}
                onChange={(e) => setFormData({ ...formData, forward_to_operations: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="forward">
                Forward to Operations / Enviar a Operaciones
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <Button type="button" variant="outline" onClick={() => navigate('/field/pmp-office')}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Inspection / Enviar'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PMPOfficeForm;
